'use client';

import { useEffect } from 'react';

/** Scenic motion only: foreground text stays readable and controls stay still. */
export function WebsiteHomeParallax() {
  useEffect(() => {
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    let cleanup = () => {};
    const setup = () => {
      cleanup();
      if (reduced.matches) return;
      const elements = [
        ...document.querySelectorAll<HTMLElement>(
          '#how-it-works [data-editorial-scene], #fundamental-features, [data-analytics-scene]',
        ),
      ];
      const states = elements.map((el) => ({
        el,
        x: 0,
        y: 0,
        mx: 0,
        my: 0,
        active: false,
      }));
      let frame = 0;
      const tick = () => {
        frame = 0;
        let unsettled = false;
        for (const s of states) {
          if (!s.active) continue;
          const rect = s.el.getBoundingClientRect();
          const progress = Math.max(
            -1,
            Math.min(
              1,
              (innerHeight / 2 - rect.top - rect.height / 2) /
                (innerHeight / 2 + rect.height / 2),
            ),
          );
          const workflow = s.el.closest('#how-it-works');
          const tx = workflow ? progress * 32 + s.mx * 12 : s.mx * 5;
          const ty = workflow ? s.my * 5 : progress * 40;
          s.x += (tx - s.x) * 0.085;
          s.y += (ty - s.y) * 0.085;
          s.el.style.setProperty('--scene-x', `${s.x.toFixed(2)}px`);
          s.el.style.setProperty('--scene-y', `${s.y.toFixed(2)}px`);
          unsettled ||= Math.abs(tx - s.x) + Math.abs(ty - s.y) > 0.1;
        }
        if (unsettled) frame = requestAnimationFrame(tick);
      };
      const wake = () => {
        if (!frame && !document.hidden) frame = requestAnimationFrame(tick);
      };
      const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const s = states.find((s) => s.el === entry.target);
          if (s) s.active = entry.isIntersecting;
        }
        wake();
      });
      const handlers = states.map((s) => {
        s.el.dataset.homeParallax = 'true';
        observer.observe(s.el);
        const move = (e: PointerEvent) => {
          if (e.pointerType !== 'mouse') return;
          const r = s.el.getBoundingClientRect();
          s.mx = ((e.clientX - r.left) / r.width) * 2 - 1;
          s.my = ((e.clientY - r.top) / r.height) * 2 - 1;
          wake();
        };
        const leave = () => {
          s.mx = 0;
          s.my = 0;
          wake();
        };
        s.el.addEventListener('pointermove', move);
        s.el.addEventListener('pointerleave', leave);
        return () => {
          s.el.removeEventListener('pointermove', move);
          s.el.removeEventListener('pointerleave', leave);
          delete s.el.dataset.homeParallax;
          s.el.style.removeProperty('--scene-x');
          s.el.style.removeProperty('--scene-y');
        };
      });
      addEventListener('scroll', wake, { passive: true });
      addEventListener('resize', wake);
      cleanup = () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
        handlers.forEach((fn) => fn());
        removeEventListener('scroll', wake);
        removeEventListener('resize', wake);
      };
    };
    setup();
    reduced.addEventListener('change', setup);
    return () => {
      cleanup();
      reduced.removeEventListener('change', setup);
    };
  }, []);
  return null;
}
